import type { BreathworkStyle, BreathworkForm } from '../types';

// A breath style's Ranks (was "Levels") — its own practice ladder (the leaf
// under the Breathing area). Renamed Level→Rank / SMP→XP (the persisted store
// fields keep their old names to avoid a migration).
//
// Both arrays are mutable HYDRATION TARGETS: the console-managed
// xp_titles(kind='breathwork_rank') rows overwrite them on boot (name +
// min_xp per rank; the highest active row IS the cap — adding a row raises
// it). These literals are the offline/fallback defaults. The final rank
// renders style-specific ("<style> Hashira").
export const BREATHWORK_RANK_TITLES: string[] = [
  'Initiate',
  'Apprentice',
  'Acolyte',
  'Practitioner',
  'Seeker',
  'Adept',
  'Disciple',
  'Warrior',
  'Sage',
  'Master',
  'Hashira',
];

export const XP_THRESHOLDS: number[] = [
  0,
  100,
  300,
  600,
  1000,
  1500,
  2100,
  2800,
  3600,
  4500,
  5500
];

// The cap — the last rank on the (config-hydrated) ladder.
export function getMaxBreathworkRank(): number {
  return XP_THRESHOLDS.length - 1;
}

export function hydrateBreathworkRanks(
  rows: Array<{ min_level: number; min_xp: number | null; name: string }>,
): void {
  const ladder = rows
    .filter((r) => Number.isFinite(r.min_level) && r.min_xp !== null && Number.isFinite(r.min_xp))
    .sort((a, b) => a.min_level - b.min_level);
  // Only accept a coherent ladder: starts at rank 0, no gaps.
  if (ladder.length < 2 || ladder[0].min_level !== 0) return;
  if (ladder.some((r, i) => r.min_level !== i)) return;
  XP_THRESHOLDS.length = 0;
  XP_THRESHOLDS.push(...ladder.map((r) => Number(r.min_xp)));
  BREATHWORK_RANK_TITLES.length = 0;
  BREATHWORK_RANK_TITLES.push(...ladder.map((r) => r.name));
}

export function calculateRankFromXP(xp: number): number {
  for (let i = XP_THRESHOLDS.length - 1; i >= 0; i--) {
    if (xp >= XP_THRESHOLDS[i]) {
      return i;
    }
  }
  return 0;
}

export function getRankTitle(styleId: string, rank: number, styleName: string, styleIcon: string): string {
  const max = getMaxBreathworkRank();
  if (rank >= max) {
    return `${styleIcon} ${styleName} ${BREATHWORK_RANK_TITLES[max] || 'Hashira'}`;
  }
  return `${styleIcon} ${BREATHWORK_RANK_TITLES[rank] || 'Initiate'}`;
}

export function getXPForNextRank(currentRank: number): number {
  const max = getMaxBreathworkRank();
  if (currentRank >= max) {
    return XP_THRESHOLDS[max];
  }
  return XP_THRESHOLDS[currentRank + 1];
}

export function getProgressToNextRank(xp: number, currentRank: number): { current: number; needed: number; percent: number } {
  const max = getMaxBreathworkRank();
  if (currentRank >= max) {
    return {
      current: xp,
      needed: XP_THRESHOLDS[max],
      percent: 100
    };
  }

  const rankStart = XP_THRESHOLDS[currentRank] || 0;
  const rankEnd = XP_THRESHOLDS[currentRank + 1] || XP_THRESHOLDS[XP_THRESHOLDS.length - 1];
  const rankRange = rankEnd - rankStart;
  const currentProgress = Math.max(0, xp - rankStart);
  const percent = rankRange > 0 ? Math.min(100, Math.max(0, (currentProgress / rankRange) * 100)) : 0;

  return {
    current: currentProgress,
    needed: rankRange,
    percent: isNaN(percent) ? 0 : percent
  };
}

export const BREATHWORK_STYLES: BreathworkStyle[] = [
  {
    id: 'stone',
    name: 'Stone',
    slug: 'stone',
    subtitle: 'Grounding / Stability',
    description: 'Ground yourself with steady, structured breathing that builds unshakeable stability and presence.',
    theme: 'Use when you need focus, grounding, or emotional stability. Perfect for centering before important tasks.',
    symbolism: 'Stone represents strength, permanence, and unwavering resolve. This style connects you to the earth\'s solid foundation.',
    color: '#6b7280',
    icon: '🪨',
    order: 1
  },
  {
    id: 'water',
    name: 'Water',
    slug: 'water',
    subtitle: 'Calm / Flow',
    description: 'Flow with gentle, calming rhythms that wash away stress and bring peace to your mind and body.',
    theme: 'Use when you need to relax, reduce anxiety, or transition into rest. Ideal for winding down or managing stress.',
    symbolism: 'Water represents fluidity, cleansing, and emotional balance. This style teaches you to move with grace through life\'s challenges.',
    color: '#3b82f6',
    icon: '🌊',
    order: 2
  },
  {
    id: 'flame',
    name: 'Flame',
    slug: 'flame',
    subtitle: 'Energizing / Activation',
    description: 'Ignite your inner fire with powerful, energizing breath techniques that awaken your body and sharpen your mind.',
    theme: 'Use when you need energy, motivation, or mental clarity. Perfect for starting your day or overcoming afternoon fatigue.',
    symbolism: 'Fire represents transformation, passion, and vitality. This style harnesses the primal energy of flame to fuel your ambitions.',
    color: '#ef4444',
    icon: '🔥',
    order: 3
  },
  {
    id: 'wind',
    name: 'Wind',
    slug: 'wind',
    subtitle: 'Balance / Clarity',
    description: 'Dance with dynamic breathing patterns that bring balance, clarity, and mental flexibility.',
    theme: 'Use when you need mental clarity, emotional balance, or a reset. Perfect for transitions and decision-making.',
    symbolism: 'Wind represents change, freedom, and the breath of life itself. This style teaches adaptability and presence.',
    color: '#14b8a6',
    icon: '🌬️',
    order: 4
  },
  {
    id: 'thunder',
    name: 'Thunder',
    slug: 'thunder',
    subtitle: 'Power / Burst',
    description: 'Harness explosive power with intense breathing techniques that build confidence and breakthrough resistance.',
    theme: 'Use when you need courage, to overcome fear, or push through barriers. Ideal before challenges or performances.',
    symbolism: 'Thunder represents power, breakthrough, and the force that shatters limitations. This style awakens your warrior spirit.',
    color: '#facc15',
    icon: '⚡',
    order: 5
  },
  {
    id: 'moon',
    name: 'Moon',
    slug: 'moon',
    subtitle: 'Tranquility / Reflection',
    description: 'Journey into deep tranquility with lunar breathing patterns designed for profound rest and restoration.',
    theme: 'Use before sleep or during deep meditation. Ideal for recovery and accessing deeper states of consciousness.',
    symbolism: 'Moon represents cycles, intuition, and the mysterious depths of rest. This style honors the power of stillness.',
    color: '#a855f7',
    icon: '🌙',
    order: 6
  },
  {
    id: 'sun',
    name: 'Sun',
    slug: 'sun',
    subtitle: 'Vitality / Mastery',
    description: 'Master advanced techniques that cultivate resilience, endurance, and radiant vitality.',
    theme: 'Use for building mental toughness, stress resilience, and mastery. This is the ultimate breathwork challenge.',
    symbolism: 'Sun represents illumination, life force, and the peak of power. This style is the path to breathwork mastery.',
    color: '#f59e0b',
    icon: '☀️',
    order: 7
  }
];

// ─────────────────────────────────────────────────────────────────────────────
// Breathwork v2 — the complete 7×7 Forms catalog (49 Forms, 7 per Style).
// Authored verbatim from docs/BREATHWORK_FRAMEWORK.md §K (the source of truth);
// Style identities §J; safety tiers §H; parameter schema §M; MVP picks §L.
//
// Each Style's Form 1 is its safest beginner entry (§L). Forms ascend 1→7 and the
// unlock ladder derives from `formNumber` (Form N unlocks at style Rank N — wired
// in BW1), so `difficulty` here is a badge, not the gate. A `makeForm` factory
// fills sensible defaults (nasal/seated/idle, computed duration & speed) so each
// literal carries only what makes the Form distinct.
// ─────────────────────────────────────────────────────────────────────────────

type StyleTheme = {
  visualization: BreathworkForm['visualization'];
  audioCue: BreathworkForm['audioCue'];
  visualTheme: string;
  audioTheme: string;
  attention: BreathworkForm['attentionLocation'];
};

const STYLE_THEME: Record<string, StyleTheme> = {
  stone:   { visualization: 'stone',   audioCue: 'count',  visualTheme: 'stone',   audioTheme: 'drone',      attention: 'whole_body' },
  water:   { visualization: 'water',   audioCue: 'ocean',  visualTheme: 'water',   audioTheme: 'waves',      attention: 'belly' },
  flame:   { visualization: 'fire',    audioCue: 'whoosh', visualTheme: 'flame',   audioTheme: 'percussion', attention: 'solar_plexus' },
  wind:    { visualization: 'wind',    audioCue: 'whoosh', visualTheme: 'wind',    audioTheme: 'airy',       attention: 'nasal_passages' },
  thunder: { visualization: 'thunder', audioCue: 'roar',   visualTheme: 'thunder', audioTheme: 'storm',      attention: 'solar_plexus' },
  moon:    { visualization: 'moon',    audioCue: 'chime',  visualTheme: 'moon',    audioTheme: 'lowhum',     attention: 'heart' },
  sun:     { visualization: 'sun',     audioCue: 'hum',    visualTheme: 'sun',     audioTheme: 'harmonic',   attention: 'heart' },
};

const slugify = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function speedFromPeriod(activeSeconds: number): BreathworkForm['speedTag'] {
  if (activeSeconds <= 1.5) return 'burst';
  if (activeSeconds <= 3) return 'fast';
  if (activeSeconds <= 8) return 'medium';
  if (activeSeconds <= 11) return 'slow';
  return 'very_slow';
}

function defaultSafetyNotes(level: BreathworkForm['safetyLevel']): string {
  switch (level) {
    case 'expert':
      return 'EXPERT-ONLY. Never practice in or near water, while driving, or standing — sit or lie down somewhere safe to faint. Stop at once on dizziness, tingling, chest pain, or panic.';
    case 'restricted':
      return 'Sit or lie down in a safe place. Never practice near water or while driving. Stop immediately if you feel dizzy, tingling, or short of breath.';
    case 'caution':
      return 'Keep effort easy and stay within comfort. Stop if you feel dizzy, lightheaded, or anxious.';
    default:
      return 'A gentle, beginner-safe practice. Breathe smoothly and naturally; stop if anything feels off.';
  }
}

// Required, Form-distinguishing inputs; everything else falls back to a default.
type FormInput = {
  styleId: string;
  formNumber: number;
  name: string;
  difficulty: BreathworkForm['difficulty'];
  intendedOutcome: BreathworkForm['intendedOutcome'];
  intensityBand: BreathworkForm['intensityBand'];
  description: string;
  inhaleSeconds: number;
  exhaleSeconds: number;
  cycles: number;
  scienceBasis: string;
  expectedEffect: string;
  recommendedDuration: string;
  safetyLevel: BreathworkForm['safetyLevel'];
  evidenceStrength: BreathworkForm['evidenceStrength'];
  cueText: string[];
  contraindications: string[];
} & Partial<BreathworkForm>;

function makeForm(input: FormInput): BreathworkForm {
  const theme = STYLE_THEME[input.styleId];
  const holdInSeconds = input.holdInSeconds ?? 0;
  const holdOutSeconds = input.holdOutSeconds ?? 0;
  const rounds = input.rounds ?? 1;
  const restBetweenRoundsSeconds = input.restBetweenRoundsSeconds ?? 0;
  const period = input.inhaleSeconds + holdInSeconds + input.exhaleSeconds + holdOutSeconds;
  const targetDurationSeconds =
    input.targetDurationSeconds ??
    Math.round(period * input.cycles * rounds + restBetweenRoundsSeconds * Math.max(0, rounds - 1));

  return {
    id: input.id ?? `${input.styleId}-${slugify(input.name)}`,
    styleId: input.styleId,
    name: input.name,
    formNumber: input.formNumber,
    difficulty: input.difficulty,
    description: input.description,
    intendedOutcome: input.intendedOutcome,
    intensityBand: input.intensityBand,
    targetDurationSeconds,
    cycles: input.cycles,
    rounds,
    inhaleSeconds: input.inhaleSeconds,
    holdInSeconds,
    exhaleSeconds: input.exhaleSeconds,
    holdOutSeconds,
    restBetweenRoundsSeconds,
    inhaleSegments: input.inhaleSegments,
    exhaleSegments: input.exhaleSegments,
    specialPhase: input.specialPhase ?? 'none',
    progressiveSteps: input.progressiveSteps,
    depth: input.depth ?? 'diaphragm',
    volume: input.volume ?? 'medium',
    pathwayIn: input.pathwayIn ?? 'nose',
    pathwayOut: input.pathwayOut ?? 'nose',
    resistance: input.resistance ?? 'none',
    sound: input.sound,
    posture: input.posture ?? 'seated',
    mode: input.mode ?? 'idle',
    movementSync: input.movementSync,
    cadenceRatio: input.cadenceRatio,
    speedTag: input.speedTag ?? speedFromPeriod(input.inhaleSeconds + input.exhaleSeconds),
    attentionLocation: input.attentionLocation ?? theme.attention,
    visualization: input.visualization ?? theme.visualization,
    audioCue: input.audioCue ?? theme.audioCue,
    visualTheme: input.visualTheme ?? theme.visualTheme,
    audioTheme: input.audioTheme ?? theme.audioTheme,
    scienceBasis: input.scienceBasis,
    expectedEffect: input.expectedEffect,
    recommendedDuration: input.recommendedDuration,
    safetyLevel: input.safetyLevel,
    evidenceStrength: input.evidenceStrength,
    cueText: input.cueText,
    contraindications: input.contraindications,
    safetyNotes: input.safetyNotes ?? defaultSafetyNotes(input.safetyLevel),
  };
}

const SAFE_FOR_EVERYONE = ['None — safe for everyone'];

export const BREATHWORK_FORMS: BreathworkForm[] = [
  // ── Stone — grounded stability, composure, endurance (bracing is the ceiling) ──
  makeForm({
    styleId: 'stone', formNumber: 1, name: 'Foundation Breath', difficulty: 'beginner',
    intendedOutcome: 'grounding', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'strong',
    description: 'The default grounding breath: a slow inhale and a longer exhale, all through the nose. Teaches diaphragmatic mechanics — the base of every other Form.',
    inhaleSeconds: 4, exhaleSeconds: 6, cycles: 12,
    scienceBasis: 'An extended exhale raises vagal influence on the heart; diaphragmatic mechanics deepen ventilation comfortably.',
    expectedEffect: 'A calm, grounded baseline.', recommendedDuration: '3–5 min',
    cueText: ['Breathe into your lower ribs and belly — not your shoulders.', 'Let the out-breath be longer and effortless.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'stone', formNumber: 2, name: 'Square Stone', difficulty: 'beginner',
    intendedOutcome: 'grounding', intensityBand: 'low', safetyLevel: 'caution', evidenceStrength: 'moderate',
    description: 'Box breathing — equal four-second inhale, hold, exhale, hold. A symmetrical structure that anchors attention under mild pressure.',
    inhaleSeconds: 4, holdInSeconds: 4, exhaleSeconds: 4, holdOutSeconds: 4, cycles: 10, speedTag: 'medium',
    depth: 'full', attentionLocation: 'whole_body',
    scienceBasis: 'A symmetrical box structure anchors attention and stabilizes HRV without sedation.',
    expectedEffect: 'Steady focus and composure.', recommendedDuration: '4–5 min',
    cueText: ['Trace a square: in, hold, out, hold.', 'Anxious? Drop the holds and breathe 4-0-4-0.'],
    contraindications: ['Anxiety — shorten or drop the holds'],
  }),
  makeForm({
    styleId: 'stone', formNumber: 3, name: 'Deep Pillar', difficulty: 'intermediate',
    intendedOutcome: 'grounding', intensityBand: 'moderate', safetyLevel: 'caution', evidenceStrength: 'moderate',
    description: 'A slow breath with a short hold after the inhale, sat or stood tall — solid, upright composure.',
    inhaleSeconds: 5, holdInSeconds: 2, exhaleSeconds: 7, cycles: 8, specialPhase: 'inhaleHold',
    posture: 'standing',
    scienceBasis: 'Slow breathing plus a mild inhale retention builds steadiness and pressure tolerance.',
    expectedEffect: 'Grounded, immovable composure.', recommendedDuration: '5–6 min',
    cueText: ['Lengthen tall through the spine.', 'Keep the brief hold soft — never strained.'],
    contraindications: ['Hypertension — skip the hold'],
  }),
  makeForm({
    styleId: 'stone', formNumber: 4, name: 'Control-Pause Builder', difficulty: 'intermediate',
    intendedOutcome: 'grounding', intensityBand: 'moderate', safetyLevel: 'caution', evidenceStrength: 'moderate',
    description: 'Light, reduced breathing with a comfortable pause after a normal exhale (the Buteyko control-pause) to gently build CO₂ tolerance.',
    inhaleSeconds: 4, exhaleSeconds: 6, holdOutSeconds: 8, cycles: 6, specialPhase: 'exhaleHold',
    scienceBasis: 'A gentle, comfortable hold after a normal exhale trains air-hunger tolerance (Buteyko).',
    expectedEffect: 'Composure and CO₂ resilience.', recommendedDuration: '6–8 min',
    cueText: ['After a relaxed exhale, pause only until the first urge to breathe.', 'Never push the hold — comfort, not a contest.'],
    contraindications: ['Asthma — keep gentle, never replace medication', 'Pregnancy or cardiovascular disease — skip'],
  }),
  makeForm({
    styleId: 'stone', formNumber: 5, name: 'March Anchor', difficulty: 'intermediate',
    intendedOutcome: 'grounding', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'moderate',
    description: 'Carry the calm into motion: breathe in for three steps, out for three, as you walk.',
    inhaleSeconds: 3, exhaleSeconds: 3, cycles: 30, targetDurationSeconds: 360,
    specialPhase: 'cadence', mode: 'movement', posture: 'walking', movementSync: 'stepCount', cadenceRatio: '3:3',
    scienceBasis: 'Locomotor–respiratory coupling transfers regulation into movement.',
    expectedEffect: 'Grounded, rhythmic movement.', recommendedDuration: '4–8 min',
    cueText: ['Match the breath to your steps: in for three, out for three.', 'Watch your footing on uneven ground.'],
    contraindications: ['None — mind uneven terrain'],
  }),
  makeForm({
    styleId: 'stone', formNumber: 6, name: 'Loaded Carry Breath', difficulty: 'advanced',
    intendedOutcome: 'grounding', intensityBand: 'moderate', safetyLevel: 'caution', evidenceStrength: 'emerging',
    description: 'An exhale-biased cadence for carries, hikes and inclines — two steps in, four steps out — to hold composure under load.',
    inhaleSeconds: 2, exhaleSeconds: 4, cycles: 40, targetDurationSeconds: 360,
    specialPhase: 'cadence', mode: 'performance', posture: 'standing', pathwayOut: 'pursed_lips',
    movementSync: 'stepCount', cadenceRatio: '2:4',
    scienceBasis: 'A longer exhale under effort supports breath economy and downshift.',
    expectedEffect: 'Steady composure under fatigue and load.', recommendedDuration: '4–8 min',
    cueText: ['Lengthen the exhale as the load gets heavy.', 'Start light; stop if you get breathless.'],
    contraindications: ['Start light; stop if breathless'],
  }),
  makeForm({
    styleId: 'stone', formNumber: 7, name: 'Braced Bedrock', difficulty: 'mastery',
    intendedOutcome: 'grounding', intensityBand: 'high', safetyLevel: 'restricted', evidenceStrength: 'strong',
    description: 'A diaphragmatic brace with a Valsalva hold against a closed glottis, on heavy reps only — maximal spinal stability for lifting.',
    inhaleSeconds: 3, holdInSeconds: 5, exhaleSeconds: 2, cycles: 5, specialPhase: 'valsalva',
    mode: 'performance', posture: 'standing', depth: 'full', volume: 'deep', pathwayOut: 'mouth', speedTag: 'medium',
    scienceBasis: 'A braced breath against a closed glottis raises intra-abdominal pressure, rigidifying the trunk for force transfer.',
    expectedEffect: 'Maximal spinal stability and power on heavy reps.', recommendedDuration: 'Per working set',
    cueText: ['Big breath into the belly; brace as if about to be punched.', 'Heavy reps only — sets of ~1–8. Release under control between reps.'],
    contraindications: ['Hypertension', 'Cardiovascular disease or aneurysm', 'Pregnancy', 'Glaucoma', 'Recent abdominal or eye surgery'],
    safetyNotes: 'Causes a brief but large blood-pressure spike. Not for the listed conditions. For heavy lifting only — never as a relaxation practice.',
  }),

  // ── Water — calm, flow, emotional regulation, coherence (the safest Style) ──
  makeForm({
    styleId: 'water', formNumber: 1, name: 'Still Pond', difficulty: 'beginner',
    intendedOutcome: 'calm', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'strong',
    description: 'Smooth, equal breathing at about six breaths a minute — resonance breathing, the single best-evidenced calming pattern.',
    inhaleSeconds: 5, exhaleSeconds: 5, cycles: 30, speedTag: 'slow',
    scienceBasis: 'Resonance breathing (~6/min) drives the baroreflex, producing large, smooth HRV.',
    expectedEffect: 'Coherent, even calm.', recommendedDuration: '5 min',
    cueText: ['Smooth, equal in and out — like a slow tide.', 'Let it feel easy; this isn’t about big breaths.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'water', formNumber: 2, name: 'Ebb Tide', difficulty: 'beginner',
    intendedOutcome: 'calm', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'strong',
    description: 'A slightly longer exhale than inhale to settle strong emotion — a gentle first-line downshift.',
    inhaleSeconds: 4, exhaleSeconds: 6, cycles: 10,
    scienceBasis: 'An exhale bias adds vagal rebound on top of slow pacing.',
    expectedEffect: 'Settles strong emotion.', recommendedDuration: '5 min',
    cueText: ['Make the out-breath a little longer than the in-breath.', 'Soften the jaw and shoulders on each exhale.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'water', formNumber: 3, name: 'Ocean Breath', difficulty: 'intermediate',
    intendedOutcome: 'calm', intensityBand: 'low', safetyLevel: 'caution', evidenceStrength: 'moderate',
    description: 'Resonance breathing with a soft throat-narrowed “ocean” sound (ujjayi) as an audible attentional anchor.',
    inhaleSeconds: 5, exhaleSeconds: 5, cycles: 30, specialPhase: 'ujjayi', resistance: 'ujjayi', sound: 'ujjayi',
    speedTag: 'slow', mode: 'idle',
    scienceBasis: 'Gentle glottal resistance plus slow pacing gives an audible anchor for attention.',
    expectedEffect: 'Focused, flowing calm.', recommendedDuration: '5–8 min',
    cueText: ['Whisper the breath at the back of the throat, like fogging a mirror.', 'Keep the sound soft and even.'],
    contraindications: ['Throat irritation', 'Low blood pressure'],
  }),
  makeForm({
    styleId: 'water', formNumber: 4, name: 'Tidal Coherence', difficulty: 'intermediate',
    intendedOutcome: 'calm', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'strong',
    description: 'Find your personal resonance frequency: try paces near 5–6 breaths a minute and settle on the smoothest.',
    inhaleSeconds: 5.5, exhaleSeconds: 5.5, cycles: 33, speedTag: 'slow',
    scienceBasis: 'Personal resonance frequency varies (≈4.5–6.5/min); calibrating to it maximizes HRV.',
    expectedEffect: 'Peak personal coherence.', recommendedDuration: '6 min',
    cueText: ['Try paces near five to six breaths a minute.', 'Settle on the rate where breathing feels effortless.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'water', formNumber: 5, name: 'River Channel', difficulty: 'intermediate',
    intendedOutcome: 'balance', intensityBand: 'low', safetyLevel: 'caution', evidenceStrength: 'moderate',
    description: 'Alternate-nostril breathing — in one nostril, out the other, switching each cycle — for balance and centering.',
    inhaleSeconds: 4, exhaleSeconds: 4, cycles: 18, specialPhase: 'nostrilSwitch',
    pathwayIn: 'alt_nostril', pathwayOut: 'alt_nostril',
    scienceBasis: 'Alternate-nostril breathing supports autonomic balance and attention.',
    expectedEffect: 'Balanced, clear, calm.', recommendedDuration: '6 min',
    cueText: ['In one nostril, out the other; switch each cycle.', 'No hands? Just imagine the side switching.'],
    contraindications: ['Nasal congestion'],
  }),
  makeForm({
    styleId: 'water', formNumber: 6, name: 'Flowing Walk', difficulty: 'advanced',
    intendedOutcome: 'calm', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'emerging',
    description: 'Coherence on the move: breathe in for four steps, out for four, as you walk — a bridge from idle practice to daily life.',
    inhaleSeconds: 4, exhaleSeconds: 4, cycles: 60, targetDurationSeconds: 480,
    specialPhase: 'cadence', mode: 'movement', posture: 'walking', movementSync: 'stepCount', cadenceRatio: '4:4',
    scienceBasis: 'Applies equal-ratio breathing to locomotion.',
    expectedEffect: 'Mobile, portable calm.', recommendedDuration: '8–10 min',
    cueText: ['Breathe in for four steps, out for four.', 'Let the rhythm carry you; nasal where you can.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'water', formNumber: 7, name: 'Still Water Under Pressure', difficulty: 'mastery',
    intendedOutcome: 'calm', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'strong',
    description: 'Resonance breathing applied invisibly during real stress — discreet, eyes-open composure on demand.',
    inhaleSeconds: 5, exhaleSeconds: 5, cycles: 24, mode: 'performance', posture: 'any', speedTag: 'slow',
    scienceBasis: 'Resonance breathing applied covertly during real stress.',
    expectedEffect: 'Composure under fire.', recommendedDuration: '2–5 min',
    cueText: ['Slow the breath without anyone noticing — eyes open.', 'One smooth cycle at a time.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),

  // ── Flame — controlled activation, energy, courage (gated from Intermediate up) ──
  makeForm({
    styleId: 'flame', formNumber: 1, name: 'Spark Breath', difficulty: 'beginner',
    intendedOutcome: 'energize', intensityBand: 'moderate', safetyLevel: 'caution', evidenceStrength: 'emerging',
    description: 'A gentle energy lift: slightly faster, inhale-led pacing — the safest activation entry.',
    inhaleSeconds: 3, exhaleSeconds: 2, cycles: 20, speedTag: 'medium',
    scienceBasis: 'Slightly faster, inhale-led pacing nudges sympathetic tone up.',
    expectedEffect: 'Mild, clean alertness.', recommendedDuration: '1–2 min',
    cueText: ['Lead with the in-breath; keep effort submaximal.', 'Stop if you feel lightheaded.'],
    contraindications: ['Anxiety', 'Hypertension'],
  }),
  makeForm({
    styleId: 'flame', formNumber: 2, name: 'Solar Rise', difficulty: 'beginner',
    intendedOutcome: 'energize', intensityBand: 'moderate', safetyLevel: 'caution', evidenceStrength: 'emerging',
    description: 'Right-nostril-only breathing for clean, heating alertness (energetics are traditional).',
    inhaleSeconds: 3, exhaleSeconds: 3, cycles: 12, pathwayIn: 'rightNostril', pathwayOut: 'rightNostril',
    scienceBasis: 'Right-nostril breathing is traditionally activating (evidence mixed).',
    expectedEffect: 'Warming alertness.', recommendedDuration: '3–4 min',
    cueText: ['Close the left nostril; breathe through the right only.', 'Stop on any nasal strain or headache.'],
    contraindications: ['Hypertension', 'Anxiety', 'Pregnancy'],
  }),
  makeForm({
    styleId: 'flame', formNumber: 3, name: 'Kindling', difficulty: 'intermediate',
    intendedOutcome: 'energize', intensityBand: 'moderate', safetyLevel: 'caution', evidenceStrength: 'emerging',
    description: 'Slow bellows breathing in short rounds — a warm-up arousal below full Bhastrika load. Seated only.',
    inhaleSeconds: 2, exhaleSeconds: 2, cycles: 15, rounds: 3, restBetweenRoundsSeconds: 40,
    volume: 'deep', speedTag: 'fast',
    scienceBasis: 'Gentle bellows breathing raises arousal below full Bhastrika load.',
    expectedEffect: 'Wakeful drive.', recommendedDuration: '3 min',
    cueText: ['Active but smooth — equal force in and out.', 'Seated only. Abort on tingling or dizziness.'],
    contraindications: ['Pregnancy', 'Cardiovascular disease', 'Hypertension', 'Epilepsy', 'Panic disorder'],
  }),
  makeForm({
    styleId: 'flame', formNumber: 4, name: 'Bellows', difficulty: 'intermediate',
    intendedOutcome: 'energize', intensityBand: 'high', safetyLevel: 'restricted', evidenceStrength: 'emerging',
    description: 'Full Bhastrika — forceful breaths in and out, in rounds, for strong activation and heat. Never first-session content.',
    inhaleSeconds: 1, exhaleSeconds: 1, cycles: 20, rounds: 3, restBetweenRoundsSeconds: 50,
    volume: 'deep', speedTag: 'burst',
    scienceBasis: 'Forceful breaths in and out raise heart rate, blood pressure and sympathetic drive.',
    expectedEffect: 'Strong activation and heat.', recommendedDuration: '5 min',
    cueText: ['Pump from the belly, equal force in and out.', 'Stop at once if you feel faint.'],
    contraindications: ['Pregnancy', 'Hypertension', 'Cardiovascular disease', 'Epilepsy', 'Glaucoma', 'Panic disorder'],
  }),
  makeForm({
    styleId: 'flame', formNumber: 5, name: 'Skull-Shining', difficulty: 'advanced',
    intendedOutcome: 'energize', intensityBand: 'high', safetyLevel: 'restricted', evidenceStrength: 'emerging',
    description: 'Kapalabhati — sharp active exhales about once a second with passive inhales, in rounds. Build up gradually.',
    inhaleSeconds: 0.6, exhaleSeconds: 0.4, cycles: 30, rounds: 3, restBetweenRoundsSeconds: 30, speedTag: 'burst',
    scienceBasis: 'Rapid expiratory pumping is stimulating and shifts EEG toward alert.',
    expectedEffect: 'Energized mental clarity.', recommendedDuration: '4–5 min',
    cueText: ['Sharp active sniff-out from the belly; let the inhale fall in passively.', 'Build up gradually; ease off if you feel pressure.'],
    contraindications: ['Pregnancy', 'Hypertension', 'Cardiovascular disease', 'Epilepsy', 'Hernia', 'Recent surgery'],
  }),
  makeForm({
    styleId: 'flame', formNumber: 6, name: 'Heat March', difficulty: 'intermediate',
    intendedOutcome: 'energize', intensityBand: 'moderate', safetyLevel: 'caution', evidenceStrength: 'emerging',
    description: 'Activation in movement: a brisk walk with two steps in, two steps out — easier than fast seated breathing.',
    inhaleSeconds: 2, exhaleSeconds: 2, cycles: 75, targetDurationSeconds: 300,
    specialPhase: 'cadence', mode: 'movement', posture: 'walking', movementSync: 'stepCount', cadenceRatio: '2:2',
    pathwayOut: 'mouth', speedTag: 'fast',
    scienceBasis: 'Couples a higher cadence with upright movement for safer energizing.',
    expectedEffect: 'Embodied drive.', recommendedDuration: '4–6 min',
    cueText: ['Brisk walk; breath synced two steps in, two out.', 'Keep away from traffic and hazards.'],
    contraindications: ['None — keep away from traffic hazards'],
  }),
  makeForm({
    styleId: 'flame', formNumber: 7, name: 'Inner Fire', difficulty: 'mastery',
    intendedOutcome: 'energize', intensityBand: 'high', safetyLevel: 'expert', evidenceStrength: 'traditional',
    description: 'Tummo-inspired: rounds of forceful breathing then an inhale retention with heat visualization. Top of the Flame ladder.',
    inhaleSeconds: 1.5, exhaleSeconds: 1.5, holdInSeconds: 12, cycles: 30, rounds: 3, restBetweenRoundsSeconds: 20,
    specialPhase: 'inhaleHold', volume: 'deep', speedTag: 'burst',
    scienceBasis: 'Tummo-inspired forceful breathing plus retention and visualization.',
    expectedEffect: 'Generated heat and focused arousal.', recommendedDuration: '8–10 min',
    cueText: ['Thirty bellows breaths, then hold the inhale and picture heat at the core.', 'Never near water. Sit or lie down; stop on dizziness.'],
    contraindications: ['Pregnancy', 'Cardiovascular disease', 'Hypertension', 'Epilepsy', 'Panic disorder'],
  }),

  // ── Wind — clarity, release, refresh (the most portable Style; the sigh) ──
  makeForm({
    styleId: 'wind', formNumber: 1, name: 'Clearing Sigh', difficulty: 'beginner',
    intendedOutcome: 'balance', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'strong',
    description: 'The physiological sigh: a full inhale, a small top-up sip, then a long exhale — the fastest acute reset. The app’s panic button.',
    inhaleSeconds: 2, exhaleSeconds: 6, cycles: 5, inhaleSegments: [2, 1], specialPhase: 'doubleInhale',
    pathwayOut: 'mouth', posture: 'any',
    scienceBasis: 'A double inhale reinflates collapsed alveoli; the long exhale offloads CO₂ fast — the quickest acute reset.',
    expectedEffect: 'Rapid clarity and calm.', recommendedDuration: '1–5 min',
    cueText: ['Two inhales through the nose — a breath, then a small top-up sip.', 'Long, slow release through the mouth.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'wind', formNumber: 2, name: 'Cyclic Sighing', difficulty: 'beginner',
    intendedOutcome: 'balance', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'strong',
    description: 'Repeated physiological sighs for about five minutes — exhale-focused breathing that lifts mood (the strongest desk reset).',
    inhaleSeconds: 2, exhaleSeconds: 7, cycles: 30, inhaleSegments: [2, 1], specialPhase: 'doubleInhale',
    pathwayOut: 'mouth',
    scienceBasis: 'Repeated exhale-focused sighing lifted mood and lowered breathing rate in an RCT.',
    expectedEffect: 'Refreshed and lighter.', recommendedDuration: '5 min',
    cueText: ['Keep the double-inhale, long-exhale rhythm going gently.', 'Let each sigh soften the body a little more.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'wind', formNumber: 3, name: 'Humming Breeze', difficulty: 'beginner',
    intendedOutcome: 'balance', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'moderate',
    description: 'Bhramari humming — a nasal inhale and a hummed exhale — to refresh and clear the head.',
    inhaleSeconds: 4, exhaleSeconds: 6, cycles: 8, specialPhase: 'hum', sound: 'hum', pathwayOut: 'hum', audioCue: 'hum',
    scienceBasis: 'Humming raises nasal nitric oxide ~15× and adds a calming vibration.',
    expectedEffect: 'Clear and refreshed.', recommendedDuration: '5 min',
    cueText: ['Inhale through the nose; hum the whole way out.', 'Feel the buzz in the face and sinuses.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'wind', formNumber: 4, name: 'Segmented Reset', difficulty: 'intermediate',
    intendedOutcome: 'balance', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'emerging',
    description: 'A stepped “sip” inhale in three parts, then one smooth exhale (viloma) — to unstick shallow breathing.',
    inhaleSeconds: 3, exhaleSeconds: 6, cycles: 10, inhaleSegments: [1, 1, 1],
    scienceBasis: 'A stepped inhale broadens ribcage awareness and resets shallow breathing.',
    expectedEffect: 'Open and reset.', recommendedDuration: '2–4 min',
    cueText: ['Sip the inhale in three steps — low, mid, high.', 'One smooth exhale all the way down.'],
    contraindications: ['Keep total depth comfortable'],
  }),
  makeForm({
    styleId: 'wind', formNumber: 5, name: 'Cooling Wind', difficulty: 'intermediate',
    intendedOutcome: 'calm', intensityBand: 'low', safetyLevel: 'caution', evidenceStrength: 'traditional',
    description: 'Sitali/Sitkari — a cooling inhale across the curled tongue or teeth, exhale through the nose — to soften heat and anger.',
    inhaleSeconds: 4, exhaleSeconds: 6, cycles: 9, specialPhase: 'cooling', pathwayIn: 'teeth', pathwayOut: 'nose',
    scienceBasis: 'Inhaling across the tongue or teeth feels cooling and de-arousing (the cooling claim is subjective).',
    expectedEffect: 'Cool and settled.', recommendedDuration: '3–5 min',
    cueText: ['Curl the tongue or sip air across the teeth on the inhale.', 'Exhale gently through the nose.'],
    contraindications: ['Cold or dry air', 'Low blood pressure'],
  }),
  makeForm({
    styleId: 'wind', formNumber: 6, name: 'Walking Clear', difficulty: 'advanced',
    intendedOutcome: 'balance', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'emerging',
    description: 'Nasal cadence walking with a physiological sigh roughly every ten steps — clear-headed mobility.',
    inhaleSeconds: 4, exhaleSeconds: 6, cycles: 30, targetDurationSeconds: 300,
    specialPhase: 'doubleInhale', mode: 'movement', posture: 'walking', movementSync: 'stepCount', pathwayOut: 'mouth',
    scienceBasis: 'Combines nasal cadence walking with periodic physiological sighs.',
    expectedEffect: 'Clear-headed mobility.', recommendedDuration: '5–10 min',
    cueText: ['Nasal breathing as you walk; add a double-inhale sigh every ~10 steps.', 'Let each sigh clear the head.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'wind', formNumber: 7, name: 'Instant Reset', difficulty: 'mastery',
    intendedOutcome: 'balance', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'strong',
    description: 'One to three physiological sighs — an on-demand, under-a-minute reset for the moment you notice stress.',
    inhaleSeconds: 2, exhaleSeconds: 6, cycles: 3, inhaleSegments: [2, 1], specialPhase: 'doubleInhale',
    mode: 'performance', posture: 'any', pathwayOut: 'mouth',
    scienceBasis: 'One to three sighs drop arousal almost immediately.',
    expectedEffect: 'Reset in seconds.', recommendedDuration: 'Under 1 min',
    cueText: ['Two quick inhales, one long exhale — repeat up to three times.', 'Use it the moment you notice stress.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),

  // ── Thunder — speed, discharge, action-readiness (box at base, holds gated) ──
  makeForm({
    styleId: 'thunder', formNumber: 1, name: 'Static Charge', difficulty: 'beginner',
    intendedOutcome: 'balance', intensityBand: 'moderate', safetyLevel: 'caution', evidenceStrength: 'moderate',
    description: 'A brisk tactical box (4-4-4-4) — the only responsible Thunder beginner Form, for calm-alert readiness.',
    inhaleSeconds: 4, holdInSeconds: 4, exhaleSeconds: 4, holdOutSeconds: 4, cycles: 8, speedTag: 'medium',
    scienceBasis: 'Structured holds steady attention without sedation.',
    expectedEffect: 'Composed readiness.', recommendedDuration: '4 min',
    cueText: ['Crisp, even box: in, hold, out, hold.', 'Anxious? Shorten the holds.'],
    contraindications: ['Anxiety — shorten the holds'],
  }),
  makeForm({
    styleId: 'thunder', formNumber: 2, name: 'Combat Box', difficulty: 'intermediate',
    intendedOutcome: 'balance', intensityBand: 'moderate', safetyLevel: 'caution', evidenceStrength: 'emerging',
    description: 'The same box at a quicker tempo (3-3-3-3), standing — a faster pressure reset for a short pre-event window.',
    inhaleSeconds: 3, holdInSeconds: 3, exhaleSeconds: 3, holdOutSeconds: 3, cycles: 10, speedTag: 'medium',
    mode: 'performance', posture: 'standing',
    scienceBasis: 'The same box structure at a quicker tempo for action-readiness.',
    expectedEffect: 'Sharp composure.', recommendedDuration: '3–4 min',
    cueText: ['Quicker square — stay crisp on each corner.', 'Good for a short pre-event window.'],
    contraindications: ['Hold-sensitive users — shorten the holds'],
  }),
  makeForm({
    styleId: 'thunder', formNumber: 3, name: 'Discharge Breath', difficulty: 'intermediate',
    intendedOutcome: 'energize', intensityBand: 'moderate', safetyLevel: 'caution', evidenceStrength: 'traditional',
    description: 'A sharp inhale and a forceful, voiced “HA” exhale to release tension and build drive.',
    inhaleSeconds: 2, exhaleSeconds: 2, cycles: 10, pathwayOut: 'roar', sound: 'voiced', audioCue: 'roar',
    posture: 'standing', speedTag: 'fast',
    scienceBasis: 'A forceful voiced exhale primes the sympathetic and motor systems.',
    expectedEffect: 'Discharged and confident.', recommendedDuration: '2 min',
    cueText: ['Sharp inhale, then drive a loud “HA” out.', 'Give yourself space; a silent version works too.'],
    contraindications: ['Hypertension'],
  }),
  makeForm({
    styleId: 'thunder', formNumber: 4, name: 'Strike Exhale', difficulty: 'intermediate',
    intendedOutcome: 'energize', intensityBand: 'moderate', safetyLevel: 'caution', evidenceStrength: 'traditional',
    description: 'Ibuki/kiai — a tension inhale and a voiced exhale exactly on the effort, for power timing. Never hold the breath through the strike.',
    inhaleSeconds: 2, exhaleSeconds: 1.5, cycles: 20, pathwayOut: 'roar', sound: 'voiced', audioCue: 'roar',
    mode: 'performance', posture: 'standing', movementSync: 'repCount', speedTag: 'fast',
    scienceBasis: 'Exhale-on-effort plus anti-breath-holding trains power timing.',
    expectedEffect: 'Powerful, well-timed output.', recommendedDuration: '2–5 min',
    cueText: ['Sharp voiced exhale exactly on the strike.', 'Never hold the breath through the effort.'],
    contraindications: ['Hypertension'],
  }),
  makeForm({
    styleId: 'thunder', formNumber: 5, name: 'Round Reset', difficulty: 'intermediate',
    intendedOutcome: 'balance', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'moderate',
    description: 'An exhale-biased recovery (2-0-4-0) between bouts — short in, long out — for a fast parasympathetic rebound.',
    inhaleSeconds: 2, exhaleSeconds: 4, cycles: 10, pathwayOut: 'pursed_lips', posture: 'standing',
    scienceBasis: 'An extended exhale speeds the parasympathetic rebound after effort.',
    expectedEffect: 'Fast recovery between bouts.', recommendedDuration: '1–2 min',
    cueText: ['Short in, long out through pursed lips.', 'Drop the shoulders and reset.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'thunder', formNumber: 6, name: 'Rolling Thunder', difficulty: 'advanced',
    intendedOutcome: 'energize', intensityBand: 'high', safetyLevel: 'restricted', evidenceStrength: 'emerging',
    description: 'A round of deep breaths then a brief comfortable exhale-hold (mild hyperventilation), twice — a controlled surge. Sit or lie down.',
    inhaleSeconds: 2, exhaleSeconds: 2, holdOutSeconds: 12, cycles: 25, rounds: 2, restBetweenRoundsSeconds: 20,
    specialPhase: 'exhaleHold', posture: 'lying', pathwayOut: 'mouth', speedTag: 'fast',
    scienceBasis: 'A controlled sympathetic surge then a brief CO₂-building hold.',
    expectedEffect: 'Energized and ready.', recommendedDuration: '6 min',
    cueText: ['A round of deep breaths, then hold the empty lungs comfortably.', 'Never near water or while driving. Sit or lie down.'],
    contraindications: ['Epilepsy', 'Cardiovascular disease', 'Hypertension', 'Pregnancy', 'Panic disorder'],
  }),
  makeForm({
    styleId: 'thunder', formNumber: 7, name: 'Storm Front', difficulty: 'mastery',
    intendedOutcome: 'energize', intensityBand: 'high', safetyLevel: 'expert', evidenceStrength: 'emerging',
    description: 'Wim Hof–style: ~30 breaths, a full exhale-hold, then a ~15s recovery inhale-hold, for three rounds. The strictest gate in the app.',
    inhaleSeconds: 2, exhaleSeconds: 2, holdOutSeconds: 60, holdInSeconds: 15, cycles: 30, rounds: 3, restBetweenRoundsSeconds: 15,
    specialPhase: 'recoveryHold', posture: 'lying', pathwayOut: 'mouth', speedTag: 'fast',
    scienceBasis: 'Voluntary sympathetic activation; adrenaline and a small immune-marker shift (single small trial).',
    expectedEffect: 'Strong activation and exhilaration.', recommendedDuration: '11–15 min',
    cueText: ['Thirty deep breaths, exhale and hold; then a big inhale held ~15s to recover.', 'NEVER in or near water or while driving. Lie down; no autoplay.'],
    contraindications: ['Epilepsy', 'Cardiovascular disease', 'Hypertension', 'Pregnancy', 'Panic disorder'],
  }),

  // ── Moon — tranquility, softening, sleep (4-7-8 is an unlock, not the default) ──
  makeForm({
    styleId: 'moon', formNumber: 1, name: 'Moonlight Breath', difficulty: 'beginner',
    intendedOutcome: 'deep_rest', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'strong',
    description: 'A slow extended-exhale breath (4-0-6-0), lying or seated — the best universal sleep starter.',
    inhaleSeconds: 4, exhaleSeconds: 6, cycles: 10, posture: 'lying',
    scienceBasis: 'An extended exhale lowers arousal toward sleep onset.',
    expectedEffect: 'Tranquil and soft.', recommendedDuration: '5 min',
    cueText: ['Slow, quiet breathing; longer out than in.', 'Let the body get heavy.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'moon', formNumber: 2, name: 'Moon Drift', difficulty: 'beginner',
    intendedOutcome: 'deep_rest', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'moderate',
    description: 'A stronger exhale bias (4-0-8-0) for deeper quieting.',
    inhaleSeconds: 4, exhaleSeconds: 8, cycles: 8, posture: 'lying',
    scienceBasis: 'A stronger exhale bias deepens parasympathetic settling.',
    expectedEffect: 'Deeply quiet.', recommendedDuration: '5 min',
    cueText: ['Stretch the exhale to about double the inhale.', 'Stop if you feel air-hungry.'],
    contraindications: ['Stop if air hunger develops'],
  }),
  makeForm({
    styleId: 'moon', formNumber: 3, name: 'Humming Moon', difficulty: 'beginner',
    intendedOutcome: 'deep_rest', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'moderate',
    description: 'Bhramari humming on a long exhale (4-0-8-0) for emotional softening and sleep.',
    inhaleSeconds: 4, exhaleSeconds: 8, cycles: 7, specialPhase: 'hum', sound: 'hum', pathwayOut: 'hum', audioCue: 'hum',
    posture: 'lying',
    scienceBasis: 'Humming with a long exhale releases nitric oxide and adds a soothing vibration.',
    expectedEffect: 'Soft and soothed.', recommendedDuration: '5–6 min',
    cueText: ['Long, gentle hum on every out-breath.', 'Let the vibration melt tension.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'moon', formNumber: 4, name: 'Lunar Channel', difficulty: 'intermediate',
    intendedOutcome: 'calm', intensityBand: 'low', safetyLevel: 'caution', evidenceStrength: 'emerging',
    description: 'Left-nostril-only breathing (Chandra) for a cool, sleepward downshift (energetics are traditional).',
    inhaleSeconds: 4, exhaleSeconds: 6, cycles: 10, pathwayIn: 'leftNostril', pathwayOut: 'leftNostril', posture: 'lying',
    scienceBasis: 'Left-nostril breathing is traditionally cooling and calming (evidence mixed).',
    expectedEffect: 'Cool tranquility.', recommendedDuration: '5 min',
    cueText: ['Close the right nostril; breathe through the left only.', 'Keep it slow and soft.'],
    contraindications: ['Low blood pressure'],
  }),
  makeForm({
    styleId: 'moon', formNumber: 5, name: 'Lantern Breath', difficulty: 'intermediate',
    intendedOutcome: 'deep_rest', intensityBand: 'low', safetyLevel: 'caution', evidenceStrength: 'emerging',
    description: '4-7-8 — inhale four, hold seven, exhale eight. A familiar sleep pattern; an unlock, not the beginner default.',
    inhaleSeconds: 4, holdInSeconds: 7, exhaleSeconds: 8, cycles: 6, specialPhase: 'inhaleHold',
    pathwayOut: 'mouth', posture: 'lying',
    scienceBasis: 'A long exhale strongly engages vagal calming; a well-known sleep tool.',
    expectedEffect: 'Drowsy calm.', recommendedDuration: '3–4 min',
    cueText: ['In for four, hold for seven, out for eight.', 'Long hold unsettling? Try 4-4-8 first, or Moonlight.'],
    contraindications: ['Anxious novices — start with Moonlight Breath'],
  }),
  makeForm({
    styleId: 'moon', formNumber: 6, name: 'Body-Scan Breath', difficulty: 'advanced',
    intendedOutcome: 'deep_rest', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'moderate',
    description: 'Slow breathing (5-0-7-0) with interoceptive attention, softening one body area on each exhale.',
    inhaleSeconds: 5, exhaleSeconds: 7, cycles: 8, posture: 'lying',
    scienceBasis: 'Slow breathing with interoceptive attention aids pre-sleep release.',
    expectedEffect: 'Released and reflective.', recommendedDuration: '10 min',
    cueText: ['On each exhale, soften one body area from head to toe.', 'No rush — let attention drift downward.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'moon', formNumber: 7, name: 'Sleep Ladder', difficulty: 'advanced',
    intendedOutcome: 'deep_rest', intensityBand: 'low', safetyLevel: 'caution', evidenceStrength: 'moderate',
    description: 'A progressive exhale ladder — 4-0-6, then 4-0-8, then 4-0-10 — to drift toward sleep.',
    inhaleSeconds: 4, exhaleSeconds: 6, cycles: 6, posture: 'lying', specialPhase: 'progressive',
    progressiveSteps: [
      { inhaleSeconds: 4, holdInSeconds: 0, exhaleSeconds: 6, holdOutSeconds: 0, cycles: 6, label: 'Settle' },
      { inhaleSeconds: 4, holdInSeconds: 0, exhaleSeconds: 8, holdOutSeconds: 0, cycles: 6, label: 'Lengthen' },
      { inhaleSeconds: 4, holdInSeconds: 0, exhaleSeconds: 10, holdOutSeconds: 0, cycles: 6, label: 'Drift' },
    ],
    targetDurationSeconds: 444,
    scienceBasis: 'A gradually lengthening exhale draws arousal down toward sleep.',
    expectedEffect: 'Drifting toward sleep.', recommendedDuration: '6–10 min',
    cueText: ['Let each stage stretch the exhale a little more.', 'Stop if you feel air-hungry.'],
    contraindications: ['Stop if air hunger develops'],
  }),

  // ── Sun — vitality, integration, balanced mastery (activation + coherence) ──
  makeForm({
    styleId: 'sun', formNumber: 1, name: 'Sunrise Breath', difficulty: 'beginner',
    intendedOutcome: 'balance', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'strong',
    description: 'Upright resonance breathing (5-0-5-0) with a vitality intention — shares the pattern with Water’s Still Pond but sits tall and rises like a sunrise.',
    inhaleSeconds: 5, exhaleSeconds: 5, cycles: 30, posture: 'seated', speedTag: 'slow',
    scienceBasis: 'Resonance breathing with an upright, vitality-framed intention.',
    expectedEffect: 'Balanced, energized-calm.', recommendedDuration: '5 min',
    cueText: ['Sit tall; smooth, equal breaths.', 'Picture a slow sunrise rising with the inhale.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'sun', formNumber: 2, name: 'Balanced Sun', difficulty: 'beginner',
    intendedOutcome: 'balance', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'moderate',
    description: 'Equal-ratio breathing (4-0-4-0, sama vritti) — centered neutrality, neither up nor down too far.',
    inhaleSeconds: 4, exhaleSeconds: 4, cycles: 10,
    scienceBasis: 'Equal in/out pacing keeps you “neither up nor down too far.”',
    expectedEffect: 'Centered and even.', recommendedDuration: '5 min',
    cueText: ['Match the inhale and exhale evenly.', 'A good daily check-in.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'sun', formNumber: 3, name: 'Solar Coherence', difficulty: 'intermediate',
    intendedOutcome: 'balance', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'strong',
    description: 'Resonance breathing (5.5-0-5.5-0) with an appreciation focus — warm, coherent vitality.',
    inhaleSeconds: 5.5, exhaleSeconds: 5.5, cycles: 33, speedTag: 'slow',
    scienceBasis: 'Resonance plus an appreciation/positive-emotion focus (coherence training).',
    expectedEffect: 'Warm, coherent vitality.', recommendedDuration: '6 min',
    cueText: ['Breathe smoothly around the chest and heart.', 'Bring something good to mind partway through.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
  makeForm({
    styleId: 'sun', formNumber: 4, name: 'Radiant Hum', difficulty: 'intermediate',
    intendedOutcome: 'balance', intensityBand: 'low', safetyLevel: 'caution', evidenceStrength: 'moderate',
    description: 'An ujjayi inhale and a hummed exhale (4-0-8-0) — airway resistance plus NO-boosting humming.',
    inhaleSeconds: 4, exhaleSeconds: 8, cycles: 8, specialPhase: 'hum', resistance: 'ujjayi', sound: 'hum',
    pathwayOut: 'hum', audioCue: 'hum',
    scienceBasis: 'Combines airway resistance with NO-boosting humming.',
    expectedEffect: 'Bright and resonant.', recommendedDuration: '5–6 min',
    cueText: ['Soft ocean-sound inhale; hum the long exhale.', 'Keep the throat relaxed.'],
    contraindications: ['Throat irritation'],
  }),
  makeForm({
    styleId: 'sun', formNumber: 5, name: 'Sun Salutation Breath', difficulty: 'intermediate',
    intendedOutcome: 'balance', intensityBand: 'moderate', safetyLevel: 'caution', evidenceStrength: 'moderate',
    description: 'One ujjayi breath per movement — inhale as you rise, exhale as you fold — embodied vitality.',
    inhaleSeconds: 4, exhaleSeconds: 4, cycles: 12, specialPhase: 'ujjayi', resistance: 'ujjayi', sound: 'ujjayi',
    mode: 'movement', posture: 'standing',
    scienceBasis: 'Coupling breath to movement builds embodied regulation.',
    expectedEffect: 'Energized and integrated.', recommendedDuration: '8 min',
    cueText: ['Inhale as you rise, exhale as you fold.', 'Move gently within your range.'],
    contraindications: ['Mobility limits — go gently'],
  }),
  makeForm({
    styleId: 'sun', formNumber: 6, name: 'Activate-then-Settle', difficulty: 'advanced',
    intendedOutcome: 'mastery', intensityBand: 'moderate', safetyLevel: 'caution', evidenceStrength: 'emerging',
    description: 'A short burst of light bellows then five minutes of resonance — a brief lift then a settle, integration in one session.',
    inhaleSeconds: 1, exhaleSeconds: 1, cycles: 15, speedTag: 'burst', specialPhase: 'progressive',
    progressiveSteps: [
      { inhaleSeconds: 1, holdInSeconds: 0, exhaleSeconds: 1, holdOutSeconds: 0, cycles: 15, label: 'Activate (light bellows)' },
      { inhaleSeconds: 5, holdInSeconds: 0, exhaleSeconds: 5, holdOutSeconds: 0, cycles: 30, label: 'Settle (resonance)' },
    ],
    targetDurationSeconds: 330,
    scienceBasis: 'A brief sympathetic lift then vagal settling — integration in one session.',
    expectedEffect: 'Charged, then centered.', recommendedDuration: '8 min',
    cueText: ['First a short burst of light bellows, then long resonance breathing.', 'Skip the bellows phase if you have hypertension or are pregnant.'],
    contraindications: ['Hypertension or pregnancy — skip the activation phase'],
  }),
  makeForm({
    styleId: 'sun', formNumber: 7, name: "Master's Breath", difficulty: 'mastery',
    intendedOutcome: 'mastery', intensityBand: 'low', safetyLevel: 'safe', evidenceStrength: 'strong',
    description: 'Free mode: read your state and choose the right breath for the moment — calm, focus, or energy.',
    inhaleSeconds: 5, exhaleSeconds: 5, cycles: 30, targetDurationSeconds: 300,
    mode: 'performance', posture: 'any', speedTag: 'slow',
    scienceBasis: 'Trained interoceptive control — read your state and choose the right breath.',
    expectedEffect: 'Effortless, adaptive mastery.', recommendedDuration: 'Open',
    cueText: ['Notice what you need: calm, focus, or energy.', 'Choose the pace and ratio that fits the moment.'],
    contraindications: SAFE_FOR_EVERYONE,
  }),
];

// ─────────────────────────────────────────────────────────────────────────────
// Breathwork v2 — unlock & safety helpers (BW1)
// ─────────────────────────────────────────────────────────────────────────────

/** Special phases that are gated regardless of the Form's own safetyLevel (§M). */
export const HIGH_RISK_SPECIAL_PHASES: ReadonlyArray<BreathworkForm['specialPhase']> = [
  'valsalva',
  'recoveryHold',
  'exhaleHold',
];

/** The style rank (0-indexed `currentLevel`) at which a Form unlocks: Form N at Rank N-1. */
export function rankRequiredForForm(formNumber: number): number {
  return Math.max(0, formNumber - 1);
}

/** Form N is unlocked once the style's rank reaches N-1 (Form 1 is always free). */
export function isFormUnlocked(formNumber: number, currentLevel: number): boolean {
  return currentLevel >= rankRequiredForForm(formNumber);
}

/**
 * High-risk Forms require the mandatory acknowledgment gate (§H/§M): anything
 * restricted/expert, or any breath-hold special phase (valsalva / exhale-hold /
 * recovery-hold after hyperventilation).
 */
export function isHighRiskForm(form: BreathworkForm): boolean {
  return (
    form.safetyLevel === 'restricted' ||
    form.safetyLevel === 'expert' ||
    HIGH_RISK_SPECIAL_PHASES.includes(form.specialPhase)
  );
}

/** True when a Form lists real population contraindications (not the "None…" sentinel). */
export function hasRealContraindications(form: BreathworkForm): boolean {
  return form.contraindications.length > 0 && !form.contraindications[0].includes('None');
}

export const ACHIEVEMENT_DEFINITIONS = [
  { id: 'first-breath', name: 'First Breath', type: 'milestone', description: 'Complete your first breathwork session' },
  { id: 'ten-sessions', name: 'Breathwork Apprentice', type: 'milestone', description: 'Complete 10 breathwork sessions' },
  { id: 'hundred-minutes', name: 'Century Mark', type: 'milestone', description: 'Practice for 100 minutes total' },
  { id: 'seven-day-streak', name: 'Week Warrior', type: 'streak', description: 'Maintain a 7-day practice streak' },
  { id: 'thirty-day-streak', name: 'Monthly Master', type: 'streak', description: 'Maintain a 30-day practice streak' },
  { id: 'all-styles', name: 'Elemental Explorer', type: 'exploration', description: 'Try all 7 breathwork styles' },
  { id: 'style-master-flame', name: 'Flame Master', type: 'mastery', styleId: 'flame', description: 'Reach mastery in Flame style' },
  { id: 'style-master-water', name: 'Water Master', type: 'mastery', styleId: 'water', description: 'Reach mastery in Water style' },
  { id: 'style-master-stone', name: 'Stone Master', type: 'mastery', styleId: 'stone', description: 'Reach mastery in Stone style' },
  { id: 'style-master-moon', name: 'Moon Master', type: 'mastery', styleId: 'moon', description: 'Reach mastery in Moon style' },
  { id: 'style-master-wind', name: 'Wind Master', type: 'mastery', styleId: 'wind', description: 'Reach mastery in Wind style' },
  { id: 'style-master-thunder', name: 'Thunder Master', type: 'mastery', styleId: 'thunder', description: 'Reach mastery in Thunder style' },
  { id: 'style-master-sun', name: 'Sun Master', type: 'mastery', styleId: 'sun', description: 'Reach mastery in Sun style' },
];
